// src/pages/Support.js
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useAuth } from './context/AuthContext';
import Header from './components/Header';
import './Support.css';
import { db } from './firebase';
import { 
  collection, doc, addDoc, setDoc, getDoc, onSnapshot, 
  query, orderBy, serverTimestamp 
} from 'firebase/firestore';

const Support = () => {
  const { currentUser } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [allChats, setAllChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [anonymousId, setAnonymousId] = useState(null);
  const messagesEndRef = useRef(null);

  const isAdmin = currentUser?.isAdmin || false;

  // Generate or retrieve anonymous ID for non-logged-in users
  useEffect(() => {
    if (!currentUser) {
      let anonId = localStorage.getItem('anonymousChatId');
      if (!anonId) {
        // Generate random number between 1000 and 9999
        const randomNum = Math.floor(Math.random() * 9000) + 1000;
        anonId = `anon_${randomNum}`;
        localStorage.setItem('anonymousChatId', anonId);
      }
      setAnonymousId(anonId);
    } else {
      setAnonymousId(null);
    }
  }, [currentUser]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // === USER SIDE (including anonymous) - realtime own messages ===
  useEffect(() => {
    if (isAdmin) return;
    
    const chatId = currentUser ? currentUser.uid : anonymousId;
    if (!chatId) return;

    const q = query(
      collection(db, 'supportChats', chatId, 'messages'),
      orderBy('timestamp', 'asc')
    );
    
    const unsub = onSnapshot(q, snap => {
      setMessages(snap.docs.map(d => ({
        id: d.id,
        ...d.data(),
        isOwn: d.data().sender === 'user'
      })));
    }, (error) => {
      console.error('Error loading messages:', error);
    });
    
    return unsub;
  }, [currentUser, anonymousId, isAdmin]);

  // === ADMIN SIDE - realtime list of ALL chats ===
  useEffect(() => {
    if (!isAdmin) return;
    const q = query(collection(db, 'supportChats'), orderBy('lastActivity', 'desc'));
    const unsub = onSnapshot(q, snap => {
      setAllChats(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, [isAdmin]);

  // === ADMIN SIDE - realtime messages for selected chat ===
  useEffect(() => {
    if (!isAdmin || !selectedChat) {
      setMessages([]);
      return;
    }
    const q = query(
      collection(db, 'supportChats', selectedChat, 'messages'),
      orderBy('timestamp', 'asc')
    );
    const unsub = onSnapshot(q, snap => {
      setMessages(snap.docs.map(d => ({
        id: d.id,
        ...d.data(),
        isOwn: d.data().sender === 'admin'
      })));
    });
    return unsub;
  }, [selectedChat, isAdmin]);

  // Get display name for user
  const getUserDisplayName = () => {
    if (currentUser) {
      return currentUser.displayName || currentUser.email || 'User';
    }
    // Anonymous user
    const randomNum = anonymousId?.split('_')[1] || Math.floor(Math.random() * 9000) + 1000;
    return `User ${randomNum}`;
  };

  // USER send (including anonymous)
  const sendUserMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    const chatId = currentUser ? currentUser.uid : anonymousId;
    if (!chatId) return;

    const displayName = getUserDisplayName();

    const chatRef = doc(db, 'supportChats', chatId);
    const chatSnap = await getDoc(chatRef);
    const isNew = !chatSnap.exists();

    // Create or update chat document
    await setDoc(chatRef, {
      userName: displayName,
      userEmail: currentUser?.email || 'anonymous',
      isAnonymous: !currentUser,
      anonymousId: !currentUser ? anonymousId : null,
      lastActivity: serverTimestamp(),
      lastMessagePreview: newMessage,
      lastMessageSender: 'user',
      hasAdminReplied: false,
      createdAt: isNew ? serverTimestamp() : chatSnap.data()?.createdAt
    }, { merge: true });

    // Add message
    await addDoc(collection(chatRef, 'messages'), {
      text: newMessage,
      sender: 'user',
      uid: chatId,
      userName: displayName,
      isAnonymous: !currentUser,
      timestamp: serverTimestamp(),
      notified: false
    });

    setNewMessage('');
  };

  // ADMIN accept pending
  const acceptRequest = async (chat) => {
    await setDoc(doc(db, 'supportChats', chat.id), {
      hasAdminReplied: true
    }, { merge: true });
    setSelectedChat(chat.id);
  };

  // ADMIN send
  const sendAdminMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedChat) return;

    const chatRef = doc(db, 'supportChats', selectedChat);

    await addDoc(collection(db, 'supportChats', selectedChat, 'messages'), {
      text: newMessage,
      sender: 'admin',
      timestamp: serverTimestamp(),
      notified: false
    });

    await setDoc(chatRef, {
      lastActivity: serverTimestamp(),
      lastMessagePreview: newMessage,
      lastMessageSender: 'admin',
      hasAdminReplied: true
    }, { merge: true });

    setNewMessage('');
  };

  const pendingRequests = useMemo(() => 
    allChats.filter(c => !c.hasAdminReplied), [allChats]);
  const activeChats = useMemo(() => 
    allChats.filter(c => c.hasAdminReplied), [allChats]);

  // Chat list item display name
  const getChatDisplayName = (chat) => {
    if (chat.isAnonymous) {
      const num = chat.anonymousId?.split('_')[1] || '?';
      return `User ${num} (Anonymous)`;
    }
    return chat.userName || 'User';
  };

  return (
    <div className="support-container">
      <Header title="💬 Support Chat" />
      <div className="support-content">
        {!currentUser && !isAdmin && (
          <div className="anonymous-banner">
            <span className="anonymous-icon">👤</span>
            <span>You're chatting as {getUserDisplayName()}</span>
            <span className="anonymous-note">(messages will be saved to this browser)</span>
          </div>
        )}

        {isAdmin && (
          <div className="admin-header">
            <span className="admin-badge">👑 Admin Dashboard</span>
            {pendingRequests.length > 0 && (
              <span className="request-badge">{pendingRequests.length} new</span>
            )}
          </div>
        )}

        <div className="chat-interface">
          {/* Admin Sidebar */}
          {isAdmin && (
            <div className="admin-sidebar">
              <div className="sidebar-header">
                <h3>Pending Requests</h3>
                <span className="chat-count">{pendingRequests.length}</span>
              </div>
              <div className="requests-list">
                {pendingRequests.map(chat => (
                  <div key={chat.id} className="request-item">
                    <div className="request-info">
                      <span className="request-avatar">
                        {chat.isAnonymous ? '👤' : '👤'}
                      </span>
                      <div className="request-details">
                        <span className="request-name">{getChatDisplayName(chat)}</span>
                        {chat.isAnonymous && (
                          <span className="anonymous-tag">anonymous</span>
                        )}
                        <span className="request-preview">
                          {chat.lastMessagePreview?.substring(0, 30) || ''}...
                        </span>
                      </div>
                    </div>
                    <button onClick={() => acceptRequest(chat)} className="accept-btn">
                      Accept
                    </button>
                  </div>
                ))}
                {pendingRequests.length === 0 && (
                  <div className="no-requests"><p>No pending requests</p></div>
                )}
              </div>

              {activeChats.length > 0 && (
                <>
                  <div className="sidebar-header" style={{ marginTop: '20px' }}>
                    <h3>Active Chats</h3>
                    <span className="chat-count">{activeChats.length}</span>
                  </div>
                  <div className="chats-list">
                    {activeChats.map(chat => (
                      <div
                        key={chat.id}
                        className={`chat-item ${selectedChat === chat.id ? 'selected' : ''}`}
                        onClick={() => setSelectedChat(chat.id)}
                      >
                        <div className="chat-avatar">
                          {chat.isAnonymous ? '👤' : '👤'}
                        </div>
                        <div className="chat-info">
                          <div className="chat-name">{getChatDisplayName(chat)}</div>
                          {chat.isAnonymous && (
                            <div className="anonymous-tag-small">anonymous</div>
                          )}
                          {chat.lastMessagePreview && (
                            <div className="chat-preview">
                              {chat.lastMessagePreview.substring(0, 20)}...
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Chat Area */}
          <div className="chat-area">
            {!isAdmin && (
              <div className="chat-window">
                <div className="chat-header">
                  <div className="chat-header-info">
                    <span className="status-dot connected"></span>
                    <span>Support Chat</span>
                  </div>
                  {!currentUser && (
                    <div className="anonymous-header-note">
                      Session ID: {anonymousId}
                    </div>
                  )}
                </div>
                <div className="messages-area">
                  {messages.map(msg => (
                    <div key={msg.id} className={`message ${msg.isOwn ? 'own' : 'other'}`}>
                      <div className="message-sender">
                        {msg.isOwn ? 'You' : 'Support'}
                        {msg.isAnonymous && msg.isOwn && (
                          <span className="anonymous-badge">(anonymous)</span>
                        )}
                      </div>
                      <div className="message-bubble">
                        <div className="message-text">{msg.text}</div>
                        <div className="message-time">
                          {msg.timestamp?.toDate 
                            ? new Date(msg.timestamp.toDate()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                            : msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                        </div>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
                <form onSubmit={sendUserMessage} className="message-input-area">
                  <input
                    type="text"
                    value={newMessage}
                    onChange={e => setNewMessage(e.target.value)}
                    placeholder="Type your message..."
                    className="message-input"
                  />
                  <button type="submit" className="send-button" disabled={!newMessage.trim()}>
                    Send
                  </button>
                </form>
              </div>
            )}

            {isAdmin && selectedChat && (
              <div className="chat-window">
                <div className="chat-header">
                  <div className="chat-header-info">
                    <span className="status-dot connected"></span>
                    <span>
                      Chat with {getChatDisplayName(allChats.find(c => c.id === selectedChat))}
                    </span>
                  </div>
                </div>
                <div className="messages-area">
                  {messages.map(msg => (
                    <div key={msg.id} className={`message ${msg.isOwn ? 'own' : 'other'}`}>
                      <div className="message-sender">
                        {msg.isOwn ? 'You' : msg.userName || 'User'}
                        {msg.isAnonymous && !msg.isOwn && (
                          <span className="anonymous-badge">(anonymous)</span>
                        )}
                      </div>
                      <div className="message-bubble">
                        <div className="message-text">{msg.text}</div>
                        <div className="message-time">
                          {msg.timestamp?.toDate 
                            ? new Date(msg.timestamp.toDate()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                            : new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
                <form onSubmit={sendAdminMessage} className="message-input-area">
                  <input
                    type="text"
                    value={newMessage}
                    onChange={e => setNewMessage(e.target.value)}
                    placeholder="Type your reply..."
                    className="message-input"
                  />
                  <button type="submit" className="send-button" disabled={!newMessage.trim()}>
                    Send
                  </button>
                </form>
              </div>
            )}

            {isAdmin && !selectedChat && (
              <div className="no-chat-selected">
                <div className="no-chat-icon">💬</div>
                <h3>No Activity</h3>
                <p>Waiting for users to request support...</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Support;